import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Caption from 'LuxKit/Typography/Caption'
import { mediaQueryUp } from 'components/utils/breakpoint'
import useMerchandiseTopBrands from 'hooks/Merchandise/useMerchandiseTopBrands'
import useMerchandiseTopCategories from 'hooks/Merchandise/useMerchandiseTopCategories'
import { take } from 'lib/array/arrayUtils'
import { rem } from 'polished'
import React from 'react'
import styled from 'styled-components'
import MerchandiseSearchFormResultItem from './MerchandiseSearchFormResultItem'
import useMerchandiseList from 'hooks/Merchandise/useMerchandiseList'
import Group from 'components/utils/Group'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { getListName } from 'analytics/enhanced-ecommerce'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'

const ResultsGrid = styled.div`
  display: grid;

  ${mediaQueryUp.largeDesktop} {
    grid-template-columns: repeat(3, 1fr);
    gap: ${rem(40)}
  }
`

interface Props {
  text: string
}

function MerchandiseSearchFormNoResults(props: Props) {
  const { text } = props

  const [brands] = useMerchandiseTopBrands()
  const [categories] = useMerchandiseTopCategories()
  const [list] = useMerchandiseList({
    sortBy: 'recommended',
  })

  const trackingProductConfig = useOfferListTracking(
    getListName('OfferList', 'Merchandise Search Form No Results', 'product'), { key: 'product' },
  )
  const trackingCategoryConfig = useOfferListTracking(
    getListName('OfferList', 'Merchandise Search Form No Results', 'category'), { key: 'category' },
  )
  const trackingBrandConfig = useOfferListTracking(
    getListName('OfferList', 'Merchandise Search Form No Results', 'brand'), { key: 'brand' },
  )

  return <Group direction="vertical" gap={20} tabletGap={24}>
    <Caption variant="large">
      Oops! We don't have any products, brands or categories related to "{text}"
      <br /><br />
      Try a different search term or browse our bestsellers below.
    </Caption>
    <VerticalSpacer gap={8}>
      <Caption variant="large" weight="bold">Recommended products</Caption>
      <ResultsGrid>
        <OfferListEventsProvider tracking={trackingProductConfig}>
          {take(list, 3).map((advert, index) => (
            <MerchandiseSearchFormResultItem
              key={advert.id}
              itemId={advert.id}
              type="product"
              index={index}
            />
          ))}
        </OfferListEventsProvider>
      </ResultsGrid>
    </VerticalSpacer>
    <VerticalSpacer gap={8}>
      <Caption variant="large" weight="bold">Recommended categories</Caption>
      <ResultsGrid>
        <OfferListEventsProvider tracking={trackingCategoryConfig}>
          {take(categories, 3).map((category, index) => (
            <MerchandiseSearchFormResultItem
              key={category.id}
              itemId={category.id}
              type="category"
              index={index}
            />
          ))}
        </OfferListEventsProvider>
      </ResultsGrid>
    </VerticalSpacer>
    <VerticalSpacer gap={8}>
      <Caption variant="large" weight="bold">Recommended brands</Caption>
      <ResultsGrid>
        <OfferListEventsProvider tracking={trackingBrandConfig}>
          {take(brands, 3).map((brand, index) => (
            <MerchandiseSearchFormResultItem
              key={brand.id}
              itemId={brand.id}
              type="brand"
              index={index}
            />
          ))}
        </OfferListEventsProvider>
      </ResultsGrid>
    </VerticalSpacer>
  </Group>
}

export default MerchandiseSearchFormNoResults
